import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '@/core/firebase';
import { useAuth } from '@/core/AuthContext';
import type { Comment, WorkoutSession, WorkoutSessionItem } from '../types';

interface UseSessionReviewOptions {
  sessionId: string | undefined;
  enabled?: boolean;
}

interface UseSessionReviewReturn {
  session: WorkoutSession | null;
  sessionItems: WorkoutSessionItem[];
  comments: Comment[];
  commentText: string;
  setCommentText: (text: string) => void;
  commentItemId: string | undefined;
  setCommentItemId: (id: string | undefined) => void;
  error: string | null;
  clientName: string | null;
  commentSectionRef: React.RefObject<HTMLDivElement | null>;
  scrollToComments: () => void;
  handleCommentOnThis: (itemId: string) => void;
  addComment: () => Promise<void>;
  itemLookup: (id: string) => string | undefined;
}

export function formatSessionStatus(status: string): string {
  const statusMap: Record<string, string> = {
    'not_started': 'Not Started',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'abandoned': 'Abandoned',
    'scheduled': 'Scheduled',
  };
  return statusMap[status] || status;
}

export function useSessionReview({
  sessionId,
  enabled = true,
}: UseSessionReviewOptions): UseSessionReviewReturn {
  const { user, userData } = useAuth();
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [commentItemId, setCommentItemId] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const commentSectionRef = useRef<HTMLDivElement>(null);

  // Subscribe to session
  useEffect(() => {
    if (!sessionId || !enabled) return;

    const unsub = onSnapshot(doc(db, 'workoutSessions', sessionId), (snap) => {
      if (!snap.exists()) {
        setError('Session not found');
        return;
      }
      setSession({ id: snap.id, ...snap.data() } as WorkoutSession);
    });

    return () => unsub();
  }, [sessionId, enabled]);

  // Subscribe to comments
  useEffect(() => {
    if (!sessionId || !enabled || !session) return;
    const commentsQuery = query(
      collection(db, 'comments'),
      where('sessionId', '==', sessionId),
      where('clientId', '==', session.clientId),
      where('trainerId', '==', session.trainerId),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(commentsQuery, (snap) => {
      const docs = snap.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Comment)
      );
      setComments(docs);
    });

    return () => unsub();
  }, [sessionId, enabled, session?.clientId, session?.trainerId]);

  // Fetch client name
  useEffect(() => {
    if (!session?.clientId) return;

    const fetchClientName = async () => {
      try {
        const clientSnap = await getDoc(doc(db, 'clients', session.clientId));
        if (clientSnap.exists()) {
          const clientData = clientSnap.data();
          setClientName(clientData.displayName || clientData.email || null);
        }
      } catch (err) {
        console.error('Failed to fetch client name:', err);
      }
    };

    fetchClientName();
  }, [session?.clientId]);

  const scrollToComments = useCallback(() => {
    commentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCommentOnThis = useCallback((itemId: string) => {
    setCommentItemId((prev) => (prev === itemId ? undefined : itemId));
    commentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const addComment = useCallback(async () => {
    if (!session || !user || !userData) return;
    if (!commentText.trim()) return;

    await addDoc(collection(db, 'comments'), {
      sessionId,
      trainerId: session.trainerId,
      clientId: session.clientId,
      workoutItemId: commentItemId || null,
      authorUserId: user.uid,
      authorRole: userData.role,
      text: commentText.trim(),
      createdAt: serverTimestamp(),
    });

    setCommentText('');
    setCommentItemId(undefined);
  }, [session, user, userData, sessionId, commentText, commentItemId]);

  const sessionItems = useMemo(
    () => session?.workoutSnapshot.items || [],
    [session]
  );

  const itemLookup = useCallback(
    (id: string) => sessionItems.find((i) => i.id === id)?.exerciseName,
    [sessionItems]
  );

  return {
    session,
    sessionItems,
    comments,
    commentText,
    setCommentText,
    commentItemId,
    setCommentItemId,
    error,
    clientName,
    commentSectionRef,
    scrollToComments,
    handleCommentOnThis,
    addComment,
    itemLookup,
  };
}
